import { formatPhoneNumber } from "@/Utils/helper";

const createPlaceholder = (text) => <span style={{ color: "var(--primary-25)" }}>{text}</span>;

const createLabel = (text, bold = true) => (
  <span
    style={{
      color: "var(--primary-25)",
      fontWeight: bold ? "600" : "normal",
    }}
  >
    {text}
  </span>
);

const truncateText = (text, maxLength) => {
  return text?.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
};

const formatName = (firstName, lastName) => (
  <>
    {firstName || createPlaceholder("First")} {lastName || createPlaceholder("Last")}
  </>
);

const formatAddress = (address1, address2) => {
  if (!address1 && !address2) return createPlaceholder("Address");
  return (
    <>
      {address1 || ""}
      {address2 ? `, ${address2}` : ""}
    </>
  );
};

const formatCityStateZip = (city, state, zip) => (
  <>
    {city || createPlaceholder("City")}
    {state ? `, ${state}` : createPlaceholder(", State")}
    {zip ? ` ${zip}` : createPlaceholder(" Zip")}
  </>
);

const isPrimary = (currentId, primaryId, icon) =>
  currentId !== null && currentId !== undefined && currentId === primaryId ? (
    <img src={icon} alt="primary" className="ic-19 d-flex align-items-center height-21" />
  ) : (
    ""
  );

const mapUnavailablePanel = (message) => [
  {
    label: createLabel("Status"),
    value: createPlaceholder(message),
  },
  { label: "", value: "" },
  { label: "", value: "" },
];

const mapSimplePanel = (config, data, formatDate) => {
  const baseData = getNestedValue(data, config.basePath);

  return config.fields.map((field) => {
    const value = getNestedValue(baseData, field.path);
    const transformedValue = field.transform ? field.transform(value, formatDate, baseData) : value;

    return {
      label: createLabel(field.label, field.labelBold),
      value: transformedValue || (field.placeholder ? createPlaceholder(field.placeholder) : ""),
    };
  });
};

const mapPhoneArray = (config, data) => {
  const baseData = getNestedValue(data, config.basePath);
  const primaryId = getNestedValue(data, config.primaryPath);

  return config.contacts.map((contactKey) => {
    const contact = baseData?.[contactKey];
    return {
      label: formatPhoneNumber(contact?.phone_number) || createPlaceholder(config.placeholder),
      value: isPrimary(contact?.id, primaryId, config.primaryIcon),
    };
  });
};

const mapEmailArray = (config, data) => {
  const baseData = getNestedValue(data, config.basePath);
  const primaryId = getNestedValue(data, config.primaryPath);

  return config.contacts.map((contactKey) => {
    const contact = baseData?.[contactKey];
    const email = contact?.email;

    return {
      label: email
        ? config.truncate
          ? truncateText(email, config.truncate)
          : email
        : createPlaceholder(config.placeholder),
      value: isPrimary(contact?.id, primaryId, config.primaryIcon),
    };
  });
};

const mapAddressPanel = (config, data) => {
  const contactData = config.contactPath
    ? getNestedValue(data, `${config.basePath}.${config.contactPath}`)
    : getNestedValue(data, config.basePath);

  const mailingId = config.mailingPath ? getNestedValue(data, config.mailingPath) : null;

  return config.fields.map((field, index) => {
    switch (field.type) {
      case "name":
        return {
          label: formatName(
            getNestedValue(data, field.firstNamePath),
            getNestedValue(data, field.lastNamePath)
          ),
          value:
            index === 0 && config.mailingIcon
              ? isPrimary(contactData?.id, mailingId, config.mailingIcon)
              : "",
        };

      case "address":
        return {
          label: formatAddress(
            getNestedValue(contactData, field.address1Path),
            getNestedValue(contactData, field.address2Path)
          ),
          value: "",
        };

      case "cityStateZip":
        return {
          label: formatCityStateZip(
            getNestedValue(contactData, field.cityPath),
            getNestedValue(contactData, field.statePath),
            getNestedValue(contactData, field.zipPath)
          ),
          value: "",
        };

      default:
        return { label: "", value: "" };
    }
  });
};

const mapPanelRows = (config, data, formatDate) => {
  if (!data) return [];
  if (config.unavailableWhen && getNestedValue(data, config.unavailableWhen) === false) {
    return mapUnavailablePanel(config.unavailableText || "N/A");
  }

  switch (config.type) {
    case "simple":
      return mapSimplePanel(config, data, formatDate);

    case "phoneArray":
      return mapPhoneArray(config, data);

    case "emailArray":
      return mapEmailArray(config, data);

    case "address":
      return mapAddressPanel(config, data);

    default:
      return [];
  }
};

export const mapPanelData = (config, data, formatDate, labels) => {
  const rows = mapPanelRows(
    labels && config.type === "emailArray" ? { ...config, truncate: false } : config,
    data,
    formatDate
  );
  if (
    !labels ||
    (config.unavailableWhen && getNestedValue(data, config.unavailableWhen) === false)
  ) {
    return rows;
  }

  return rows.map((row, index) => ({
    ...row,
    label: createLabel(labels[index]),
    value: (
      <span className="client-contact-detail-value">
        {config.type === "phoneArray" && row.value}
        <span className="client-contact-detail-text">{row.label}</span>
        {config.type !== "phoneArray" && row.value}
      </span>
    ),
  }));
};
